import { useEffect, useState } from 'react';
import Dialog from '../../../components/Dialog/SideDrawer';
import { formatDate } from '../../../utils/helpers';
import { DATABASE } from '../../../config/firebase';
import * as XLSX from 'xlsx';
import saveAs from 'file-saver';

const CustomerWalletDialog = ({ customer, onClose }: { customer: any, onClose: () => void }) => {
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    let unsubscribe: any;
    if (customer?.id) {
      setLoading(true);
      unsubscribe = DATABASE.collection('customers')
        .doc(customer.id)
        .collection('wallet_transactions')
        .orderBy('createdAt', 'desc')
        .onSnapshot((snapshot: any) => {
          const txs = snapshot.docs.map((doc: any) => ({ id: doc.id, ...doc.data() }));
          setTransactions(txs);
          setLoading(false);
        });
    }
    return () => unsubscribe && unsubscribe();
  }, [customer?.id]);

  const exportToExcel = () => {
    const data = transactions.map(tx => ({
      Amount: tx.amount,
      Type: tx.type,
      Reason: tx.reason,
      'Created At': tx.createdAt ? (tx.createdAt.toDate ? formatDate(tx.createdAt.toDate()) : formatDate(tx.createdAt)) : '',
    }));
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Wallet Transactions');
    const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
    saveAs(blob, `wallet_transactions_${customer?.name || customer?.id || ''}.xlsx`);
  };

  const filteredTransactions = transactions.filter(tx => {
    // Date filter
    let passDate = true;
    if (dateFrom) {
      let createdAtDate = tx.createdAt ? (tx.createdAt.toDate ? tx.createdAt.toDate() : new Date(tx.createdAt)) : null;
      if (!createdAtDate || createdAtDate < new Date(dateFrom)) passDate = false;
    }
    if (dateTo) {
      let createdAtDate = tx.createdAt ? (tx.createdAt.toDate ? tx.createdAt.toDate() : new Date(tx.createdAt)) : null;
      if (!createdAtDate || createdAtDate > new Date(dateTo)) passDate = false;
    }
    if (!passDate) return false;
    // Search filter
    if (!search) return true;
    const searchLower = search.toLowerCase();
    const createdAtStr = tx.createdAt ? (tx.createdAt.toDate ? formatDate(tx.createdAt.toDate()) : formatDate(tx.createdAt)) : '';
    return (
      ((tx.amount !== undefined && tx.amount !== null ? tx.amount.toString() : '').toLowerCase().includes(searchLower)) ||
      ((tx.type ?? '').toLowerCase().includes(searchLower)) ||
      ((tx.reason ?? '').toLowerCase().includes(searchLower)) ||
      (typeof createdAtStr === 'string' ? createdAtStr.toLowerCase().includes(searchLower) : false)
    );
  });

  return (
    <Dialog onClose={onClose} open={true} title={`Wallet Transactions - ${customer?.name || ''}`} code={customer}>
      <div className="min-w-[350px] max-w-lg">
        {/* Search, Date Filters, and Export in one row */}
        <div className="flex flex-col gap-2 mb-2">
          <div className="flex items-center gap-2">
            <input
              type="text"
              className="border border-gray-300 rounded px-3 py-2 text-sm flex-1"
              placeholder="Search transactions..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            <button
              onClick={exportToExcel}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 16v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-2 4h6m0 0l-2-2m2 2l-2 2" />
              </svg>
              Export to Excel
            </button>
          </div>
          <div className="flex items-center gap-2">
            <input
              type="date"
              className="border border-gray-300 rounded px-2 py-1 text-sm"
              value={dateFrom}
              onChange={e => setDateFrom(e.target.value)}
              placeholder="From"
            />
            <span className="text-gray-500 text-xs">to</span>
            <input
              type="date"
              className="border border-gray-300 rounded px-2 py-1 text-sm"
              value={dateTo}
              onChange={e => setDateTo(e.target.value)}
              placeholder="To"
            />
          </div>
        </div>
        {loading ? (
          <div className="flex justify-center items-center py-8">
            <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path>
            </svg>
          </div>
        ) : filteredTransactions.length === 0 ? (
          <div className="text-center text-gray-500 py-8">No wallet transactions found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs text-left text-gray-700">
              <thead className="bg-gray-50">
                <tr>
                  <th className="py-2 px-3">Amount</th>
                  <th className="py-2 px-3">Type</th>
                  <th className="py-2 px-3">Reason</th>
                  <th className="py-2 px-3">Created At</th>
                </tr>
              </thead>
              <tbody>
                {filteredTransactions.map(tx => (
                  <tr key={tx.id} className="border-b">
                    <td className="py-2 px-3 font-semibold">{tx.amount} KD</td>
                    <td className="py-2 px-3">{tx.type}</td>
                    <td className="py-2 px-3">{tx.reason}</td>
                    <td className="py-2 px-3">{tx.createdAt ? (tx.createdAt.toDate ? formatDate(tx.createdAt.toDate()) : formatDate(tx.createdAt)) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default CustomerWalletDialog; 